import React from "react";
import {connect} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {Expandable} from "@pg-design/expandable-module";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    fadeInAnimation,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    mv,
    p,
    pr,
    truncate,
    w100
} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {form} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {Country} from "../../../region/types/Country";
import {resetFetchAllSearchLists, stopFetchAllSearchLists} from "../../actions/fetch_search_all_action";
import {optimizedFetchPlaceList} from "../../actions/fetch_search_places_action";
import {clearLabelOfActiveValue, resetActiveDropdownItem, setNextDropdownItem, setPreviousDropdownItem} from "../../actions/set_active_dropdown_item";
import {setItemOnEnter} from "../../actions/set_item_on_enter";
import {setTravelTimeData} from "../../actions/set_travel_time";
import {updateCurrentTab} from "../../actions/update_current_tab";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchAutocompleteInputWithSlot} from "./SearchAutocompleteInputWithSlot";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteFormValues,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps
} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteController} from "./useSearchAutocompleteController";

export type {ISearchAutocompleteFormValues, SearchAutocompleteController} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const SearchAutocompleteC: React.FC<IProps> = (props) => {
    const theme = useTheme();
    const controller = useSearchAutocompleteController(props);
    const selectedSearch = props.values?.search;

    return (
        <RelativeWrapper isInModal={props.isInModal} onKeyDown={controller.onKeyDownHandler} ref={controller.wrapperRef} css={controller.relativeWrapperCss}>
            {props.renderLayout ? (
                props.renderLayout(controller)
            ) : (
                <>
                    {props.isHomepage && (
                        <div css={tabsWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.COUNTRY_TAB}>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.POLAND)}
                                isActive={controller.search.currentTabData.country === Country.POLAND}
                            >
                                W Polsce
                            </StyledTab>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.SPAIN)}
                                isActive={controller.search.currentTabData.country === Country.SPAIN}
                            >
                                W Hiszpanii
                            </StyledTab>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Holiday)}
                                isActive={controller.search.currentTabData.currentTab === SearchTab.Holiday}
                            >
                                Wakacyjne
                            </StyledTab>
                        </div>
                    )}
                    <SearchbarContainer disableExtraMargins={controller.disableSearchbarExtraMargins} css={controller.searchBarContainerStyle}>
                        <SearchAutocompleteInputWithSlot {...controller.inputProps} slot={props.slot} slotOrder={props.slotOrder} />
                        {controller.shouldRenderDistanceFilter && (
                            <InputWrapper css={[p(0), distanceWrapperRightStyle]}>
                                <SearchDistanceFilter {...controller.distanceFilterProps} />
                            </InputWrapper>
                        )}
                    </SearchbarContainer>
                    <SearchDropdown {...controller.dropdownProps}>
                        <div css={searchDropdownContentWrapperStyle}>{controller.renderDropdownList()}</div>
                    </SearchDropdown>
                    {controller.showSelectedUnderInput && selectedSearch && "regions" in selectedSearch && (
                        <div css={regionsHolderStyle}>
                            {selectedSearch.regions.length > 2 ? (
                                <Expandable
                                    isExpandedByDefault={false}
                                    collapsedHeight={controller.isMobile ? 0 : 110}
                                    customCollapseButton={({isExpanded, toggleCollapse}) => (
                                        <Button
                                            variant="none_secondary"
                                            size="x-small"
                                            type="button"
                                            iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                            onClick={toggleCollapse}
                                            css={regionExpandableButtonStyle}
                                        >
                                            {isExpanded
                                                ? "Zwiń wybrane lokalizacje"
                                                : `Rozwiń wybrane lokalizacje ${selectedSearch.tabType === SearchTab.Regions ? `(${selectedSearch.regions.length})` : ""}`}
                                        </Button>
                                    )}
                                    customShadowStyle={customExpandableShadowStyle}
                                >
                                    <div css={regionsExpandableStyle}>
                                        {selectedSearch.regions.map((region) => (
                                            <Pill
                                                key={region.id}
                                                size="large"
                                                css={regionPillStyle}
                                                onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                            >
                                                {!props.includeDistanceFilter && <MapMarkerOutlineIcon fill={theme.colors.secondary} size="2.4" css={pr(1)} />}
                                                <div css={[titleHolderStyle, truncate]}>
                                                    {region.full_name_reverted}
                                                    {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                                </div>
                                            </Pill>
                                        ))}
                                    </div>
                                </Expandable>
                            ) : (
                                selectedSearch.regions.map((region) => (
                                    <Pill key={region.id} size="large" css={regionPillStyle} onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}>
                                        {!props.includeDistanceFilter && <MapMarkerOutlineIcon fill={theme.colors.secondary} size="2.4" css={pr(1)} />}
                                        <div css={[titleHolderStyle, truncate]}>
                                            {region.full_name_reverted}
                                            {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                        </div>
                                    </Pill>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}
        </RelativeWrapper>
    );
};

export const SearchAutocomplete = connect(mapStateToProps, mapActionsToProps)(form<ISearchAutocompleteFormValues, IProps>(SearchAutocompleteC));

function mapStateToProps(state: IRPStore): ISearchAutocompleteStateProps {
    return {
        search: state.search
    };
}

function mapActionsToProps(dispatch: Dispatch): ISearchAutocompleteActionsProps {
    return {
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                setNextDropdownItem,
                setPreviousDropdownItem,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                updateCurrentTab,
                optimizedFetchPlaceList,
                setItemOnEnter,
                clearLabelOfActiveValue,
                setTravelTimeData
            },
            dispatch
        )
    };
}

export const RelativeWrapper = styled.div<{isInModal?: boolean}>`
    position: relative;
    ${({isInModal}) =>
        !isInModal &&
        css`
            height: calc(100% - 6rem);
        `};
    ${p(2)};
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: column;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        padding: 0;
        max-height: 100%;
        width: 100%;
    }
`;

interface ISearchDropdown {
    isOpen: boolean;
    isInModal?: boolean;
    isInputWithDistanceFilter?: boolean;
    isHomepage?: boolean;
    activeRegionSearchTab?: RegionSearchTabsType | null;
    minWidth?: string;
    top?: string;
}

export const SearchDropdown = styled.div<ISearchDropdown>`
    width: 100%;
    display: ${(props) => (props.isOpen || props.isInModal ? "flex" : "none")};
    flex-direction: column;
    ${fadeInAnimation("0.2s")};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${elevation(4)};
    }

    ${(props) =>
        !props.isInModal &&
        css`
            position: absolute;
            ${p(1)};
            margin: 0;

            z-index: 1001;
            top: ${props.top ?? (props.isHomepage ? "11rem" : "6rem")};
            left: ${props.isInputWithDistanceFilter ? "120px" : 0};
            width: calc(100% - ${props.isInputWithDistanceFilter ? "120px" : "0px"});
            ${borderRadius(2)}
            background: #fff;

            @media (max-width: ${props.theme.breakpoints.xs}) {
                left: 0;
                width: 100%;
            }
        `};

    @media (min-width: ${getThemeBreakpoint().md}) {
        min-width: ${(props) => props.minWidth || "600px"};
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        min-width: ${(props) => (!props.minWidth && props.activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? "836px" : props.minWidth || "600px")};
    }
`;

const searchDropdownContentWrapperStyle = css`
    ${p(2, 1)};
`;

const distanceWrapperRightStyle = css`
    max-width: 120px;
    width: 30%;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-width: 0;
    height: 50px;
`;

export const SearchbarContainer = styled.div<{disableExtraMargins?: boolean}>`
    display: flex;
    align-items: center;
    width: 100%;
    ${({disableExtraMargins}) => disableExtraMargins && mb(0.5)};
    max-height: ${calculateRemSize(6)};

    > div:nth-of-type(2) {
        margin-bottom: 0;
        width: 100%;
        z-index: initial;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        ${({disableExtraMargins}) => (disableExtraMargins ? mb(0.5) : mb(3))};
    }
`;

const StyledTab = styled.div<{isActive: boolean}>`
    ${w100};
    ${borderRadius(0)};
    background-color: ${(props) => (props.isActive ? props.theme.colors.background[200] : props.theme.colors.background[100])};
    ${flexAbsoluteCenter};
    height: ${calculateRemSize(6)};
    cursor: pointer;

    :hover {
        background-color: ${(props) => props.theme.colors.background[200]};
    }
`;

const tabsWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${w100};
    ${borderRadius(2, 2, 0, 0)};
    ${backgroundColor(theme.colors.gray[200])};
    overflow: hidden;
    max-height: ${calculateRemSize(6)};
`;

const regionPillStyle = css`
    ${p(0, 1)};
    height: 4.8rem;
    ${flex("center")};
    ${flexDirection("row")};
`;

const regionsHolderStyle = () => css`
    ${mv(2)};
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: ${calculateRemSize(1)};

    div:last-of-type {
        margin-right: 0;
    }
`;

const titleHolderStyle = css`
    ${pr(1)};
    flex-grow: 1;
    text-align: left;
`;

const regionsExpandableStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const regionExpandableButtonStyle = css`
    margin-top: -${calculateRemSize(2.5)};
    width: 100%;
`;

const customExpandableShadowStyle = {
    background: `linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%)`
};
import React from "react";
import {connect} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {Expandable} from "@pg-design/expandable-module";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    fadeInAnimation,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    mv,
    p,
    pr,
    truncate,
    w100
} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {form} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {Country} from "../../../region/types/Country";
import {resetFetchAllSearchLists, stopFetchAllSearchLists} from "../../actions/fetch_search_all_action";
import {optimizedFetchPlaceList} from "../../actions/fetch_search_places_action";
import {clearLabelOfActiveValue, resetActiveDropdownItem, setNextDropdownItem, setPreviousDropdownItem} from "../../actions/set_active_dropdown_item";
import {setItemOnEnter} from "../../actions/set_item_on_enter";
import {setTravelTimeData} from "../../actions/set_travel_time";
import {updateCurrentTab} from "../../actions/update_current_tab";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchAutocompleteInputWithSlot} from "./SearchAutocompleteInputWithSlot";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteFormValues,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps
} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteController} from "./useSearchAutocompleteController";

export type {ISearchAutocompleteFormValues, SearchAutocompleteController} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const SearchAutocompleteC: React.FC<IProps> = (props) => {
    const theme = useTheme();
    const controller = useSearchAutocompleteController(props);
    const selectedSearch = props.values?.search;

    return (
        <RelativeWrapper isInModal={props.isInModal} onKeyDown={controller.onKeyDownHandler} ref={controller.wrapperRef} css={controller.relativeWrapperCss}>
            {props.renderLayout ? (
                props.renderLayout(controller)
            ) : (
                <>
                    {props.isHomepage && (
                        <div css={tabsWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.COUNTRY_TAB}>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.POLAND)}
                                isActive={controller.search.currentTabData.country === Country.POLAND}
                            >
                                W Polsce
                            </StyledTab>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.SPAIN)}
                                isActive={controller.search.currentTabData.country === Country.SPAIN}
                            >
                                W Hiszpanii
                            </StyledTab>
                            <StyledTab
                                onClick={() => controller.onTabTypeClick(SearchTab.Holiday)}
                                isActive={controller.search.currentTabData.currentTab === SearchTab.Holiday}
                            >
                                Wakacyjne
                            </StyledTab>
                        </div>
                    )}
                    <SearchbarContainer disableExtraMargins={controller.disableSearchbarExtraMargins} css={controller.searchBarContainerStyle}>
                        <SearchAutocompleteInputWithSlot {...controller.inputProps} slot={props.slot} slotOrder={props.slotOrder} />
                        {controller.shouldRenderDistanceFilter && (
                            <InputWrapper css={[p(0), distanceWrapperRightStyle]}>
                                <SearchDistanceFilter {...controller.distanceFilterProps} />
                            </InputWrapper>
                        )}
                    </SearchbarContainer>
                    <SearchDropdown {...controller.dropdownProps}>
                        <div css={searchDropdownContentWrapperStyle}>{controller.renderDropdownList()}</div>
                    </SearchDropdown>
                    {controller.showSelectedUnderInput && selectedSearch && "regions" in selectedSearch && (
                        <div css={regionsHolderStyle}>
                            {selectedSearch.regions.length > 2 ? (
                                <Expandable
                                    isExpandedByDefault={false}
                                    collapsedHeight={controller.isMobile ? 0 : 110}
                                    customCollapseButton={({isExpanded, toggleCollapse}) => (
                                        <Button
                                            variant="none_secondary"
                                            size="x-small"
                                            type="button"
                                            iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                            onClick={toggleCollapse}
                                            css={regionExpandableButtonStyle}
                                        >
                                            {isExpanded
                                                ? "Zwiń wybrane lokalizacje"
                                                : `Rozwiń wybrane lokalizacje ${selectedSearch.tabType === SearchTab.Regions ? `(${selectedSearch.regions.length})` : ""}`}
                                        </Button>
                                    )}
                                    customShadowStyle={customExpandableShadowStyle}
                                >
                                    <div css={regionsExpandableStyle}>
                                        {selectedSearch.regions.map((region) => (
                                            <Pill
                                                key={region.id}
                                                size="large"
                                                css={regionPillStyle}
                                                onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                            >
                                                {!props.includeDistanceFilter && <MapMarkerOutlineIcon fill={theme.colors.secondary} size="2.4" css={pr(1)} />}
                                                <div css={[titleHolderStyle, truncate]}>
                                                    {region.full_name_reverted}
                                                    {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                                </div>
                                            </Pill>
                                        ))}
                                    </div>
                                </Expandable>
                            ) : (
                                selectedSearch.regions.map((region) => (
                                    <Pill key={region.id} size="large" css={regionPillStyle} onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}>
                                        {!props.includeDistanceFilter && <MapMarkerOutlineIcon fill={theme.colors.secondary} size="2.4" css={pr(1)} />}
                                        <div css={[titleHolderStyle, truncate]}>
                                            {region.full_name_reverted}
                                            {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                        </div>
                                    </Pill>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}
        </RelativeWrapper>
    );
};

export const SearchAutocomplete = connect(mapStateToProps, mapActionsToProps)(form<ISearchAutocompleteFormValues, IProps>(SearchAutocompleteC));

function mapStateToProps(state: IRPStore): ISearchAutocompleteStateProps {
    return {
        search: state.search
    };
}

function mapActionsToProps(dispatch: Dispatch): ISearchAutocompleteActionsProps {
    return {
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                setNextDropdownItem,
                setPreviousDropdownItem,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                updateCurrentTab,
                optimizedFetchPlaceList,
                setItemOnEnter,
                clearLabelOfActiveValue,
                setTravelTimeData
            },
            dispatch
        )
    };
}

export const RelativeWrapper = styled.div<{isInModal?: boolean}>`
    position: relative;
    ${({isInModal}) =>
        !isInModal &&
        css`
            height: calc(100% - 6rem);
        `};
    ${p(2)};
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: column;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        padding: 0;
        max-height: 100%;
        width: 100%;
    }
`;

interface ISearchDropdown {
    isOpen: boolean;
    isInModal?: boolean;
    isInputWithDistanceFilter?: boolean;
    isHomepage?: boolean;
    activeRegionSearchTab?: RegionSearchTabsType | null;
    minWidth?: string;
    top?: string;
}

export const SearchDropdown = styled.div<ISearchDropdown>`
    width: 100%;
    display: ${(props) => (props.isOpen || props.isInModal ? "flex" : "none")};
    flex-direction: column;
    ${fadeInAnimation("0.2s")};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${elevation(4)};
    }

    ${(props) =>
        !props.isInModal &&
        css`
            position: absolute;
            ${p(1)};
            margin: 0;

            z-index: 1001;
            top: ${props.top ?? (props.isHomepage ? "11rem" : "6rem")};
            left: ${props.isInputWithDistanceFilter ? "120px" : 0};
            width: calc(100% - ${props.isInputWithDistanceFilter ? "120px" : "0px"});
            ${borderRadius(2)}
            background: #fff;

            @media (max-width: ${props.theme.breakpoints.xs}) {
                left: 0;
                width: 100%;
            }
        `};

    @media (min-width: ${getThemeBreakpoint().md}) {
        min-width: ${(props) => props.minWidth || "600px"};
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        min-width: ${(props) => (!props.minWidth && props.activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? "836px" : props.minWidth || "600px")};
    }
`;

const searchDropdownContentWrapperStyle = css`
    ${p(2, 1)};
`;

const distanceWrapperRightStyle = css`
    max-width: 120px;
    width: 30%;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-width: 0;
    height: 50px;
`;

export const SearchbarContainer = styled.div<{disableExtraMargins?: boolean}>`
    display: flex;
    align-items: center;
    width: 100%;
    ${({disableExtraMargins}) => disableExtraMargins && mb(0.5)};
    max-height: ${calculateRemSize(6)};

    > div:nth-of-type(2) {
        margin-bottom: 0;
        width: 100%;
        z-index: initial;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        ${({disableExtraMargins}) => (disableExtraMargins ? mb(0.5) : mb(3))};
    }
`;

const StyledTab = styled.div<{isActive: boolean}>`
    ${w100};
    ${borderRadius(0)};
    background-color: ${(props) => (props.isActive ? props.theme.colors.background[200] : props.theme.colors.background[100])};
    ${flexAbsoluteCenter};
    height: ${calculateRemSize(6)};
    cursor: pointer;

    :hover {
        background-color: ${(props) => props.theme.colors.background[200]};
    }
`;

const tabsWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${w100};
    ${borderRadius(2, 2, 0, 0)};
    ${backgroundColor(theme.colors.gray[200])};
    overflow: hidden;
    max-height: ${calculateRemSize(6)};
`;

const regionPillStyle = css`
    ${p(0, 1)};
    height: 4.8rem;
    ${flex("center")};
    ${flexDirection("row")};
`;

const regionsHolderStyle = () => css`
    ${mv(2)};
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: ${calculateRemSize(1)};

    div:last-of-type {
        margin-right: 0;
    }
`;

const titleHolderStyle = css`
    ${pr(1)};
    flex-grow: 1;
    text-align: left;
`;

const regionsExpandableStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const regionExpandableButtonStyle = css`
    margin-top: -${calculateRemSize(2.5)};
    width: 100%;
`;

const customExpandableShadowStyle = {
    background: `linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%)`
};
